import {
  CButton,
  CCard,
  CCardBody,
  CCardHeader,
  CCol,
  CRow,
  CLabel,
  CInputGroup,
  CInputGroupText,
  CInput,
} from "@coreui/react";
import { useEffect, useState, useCallback } from "react";
import { useHistory } from "react-router-dom";
import { getList } from "../../api/generics";
import { getAbsoluteUrl } from "../../api/urls";
import { fetchAuthenticated } from "../../api/utils";
import Asset from "../../models/asset";
import Document from "../../models/document";
import Company from "../../models/company";
import Node, {
  createCompanyNode,
  createAssetNode,
  createDocumentNode,
  isAssetNode,
  isDocumentNode,
  isCompanyNode,
  getNodeData,
} from "../../models/node";
import { getRoutePath } from "../../routes";
import { SUCCESS } from "../../utils/constants/tags";
import { successAlert, warningAlert } from "../utils/messages";
import { BsFillPlusCircleFill } from "react-icons/bs";
import { useSelector } from "react-redux";
import { RootState } from "../../store";
import PermissionRequiredComponent from "../permissions/PermissionRequiredComponent";
import {
  ASSETS_CREATE,
  ASSETS_DESTROY,
  ASSETS_UPDATE,
} from "../../auth/permissions";

// Props for the tree component using Node model
type TreeProps = {
  nodes: Node[];
  onSelect: (node: Node) => void;
  onDownloadDocument?: (doc: Document) => Promise<void>;
  selectedNodeId?: number;
  expandedNodeIds?: number[];
  downloadingDocumentId?: number | null;
  onToggleExpand: (nodeId: number) => void;
};

// TreeView Component using Node model
function AssetTreeView({
  nodes,
  onSelect,
  onDownloadDocument,
  selectedNodeId,
  expandedNodeIds = [],
  downloadingDocumentId,
  onToggleExpand,
}: TreeProps) {
  const getSubtreeDocStatus = (
    node: Node
  ): "missing" | "all-present" | "none" => {
    let totalDocs = 0;
    let missingDocs = 0;

    const walk = (n: Node) => {
      if (isDocumentNode(n)) {
        totalDocs++;
        const data = getNodeData(n) as Document;
        if (!data?.filePath || data.filePath.trim() === "") {
          missingDocs++;
        }
      }
      n.children.forEach(walk);
    };

    node.children.forEach(walk); // walk children only
    if (totalDocs === 0) return "none";
    if (missingDocs > 0) return "missing";
    return "all-present";
  };

  const TreeNodeItem = ({
    node,
    depth = 0,
    onSelect,
    onDownloadDocument,
    onToggleExpand,
  }: {
    node: Node;
    depth?: number;
    onSelect: (node: Node) => void;
    onDownloadDocument?: (doc: Document) => Promise<void>;
    onToggleExpand: (nodeId: number) => void;
  }) => {
    const isExpanded = expandedNodeIds.includes(node.node_id);
    const [isHovered, setIsHovered] = useState(false);
    const [localDownloading, setLocalDownloading] = useState(false);

    // Obtener datos específicos según el tipo de nodo
    const nodeData = getNodeData(node);
    const isAsset = isAssetNode(node);
    const isDocument = isDocumentNode(node);
    const isCompany = isCompanyNode(node);

    // Obtener el documento si es un nodo de documento
    const documentData = isDocument ? (nodeData as Document) : undefined;
    const assetData = isAsset ? (nodeData as Asset) : undefined;
    const companyData = isCompany ? (nodeData as Company) : undefined;

    // Verificar si el documento tiene archivo
    const hasFile =
      isDocument &&
      documentData?.filePath &&
      documentData.filePath.trim() !== "";

    const handleNodeClick = async () => {
      if (isAsset) {
        onSelect(node);
      } else if (isDocument && onDownloadDocument && documentData) {
        // Verificar si tiene archivo antes de descargar
        if (!hasFile) {
          warningAlert(
            "Este documento no tiene un archivo asociado para descargar."
          );
          return;
        }

        // Solo permitir una descarga a la vez
        if (localDownloading || node.node_id === downloadingDocumentId) {
          return;
        }

        setLocalDownloading(true);
        try {
          await onDownloadDocument(documentData);
        } catch (error) {
          console.error("Error downloading document:", error);
        } finally {
          setLocalDownloading(false);
        }
      }
    };

    const handleToggleExpand = (e: React.MouseEvent) => {
      e.stopPropagation();
      onToggleExpand(node.node_id);
    };

    const getNodeIcon = () => {
      switch (node.node_type) {
        case "company":
          return "🏢";
        case "asset":
          return node.children && node.children.length > 0
            ? isExpanded
              ? "📂"
              : "📁"
            : "📦";
        case "document":
          if (localDownloading || node.node_id === downloadingDocumentId) {
            return "⏳";
          }
          return hasFile ? "📄" : "📄❌";
        default:
          return "○";
      }
    };

    const getNodeTypeLabel = () => {
      switch (node.node_type) {
        case "company":
          return "Compañía";
        case "asset":
          return "Productor";
        case "document":
          return "Documento";
        default:
          return "";
      }
    };

    const getFileStatusIcon = () => {
      if (!isDocument) return null;

      if (!hasFile) {
        return (
          <span
            style={{ color: "#f44336", marginLeft: "4px" }}
            title="Sin archivo"
          >
            ⚠️
          </span>
        );
      }

      if (localDownloading || node.node_id === downloadingDocumentId) {
        return <span style={{ color: "#2196f3", marginLeft: "4px" }}>⏳</span>;
      }

      return (
        <span
          style={{ color: "#4caf50", marginLeft: "4px" }}
          title="Con archivo"
        >
          ✓
        </span>
      );
    };

    const getParentFileStatusIcon = () => {
      if (isDocument) return null; // handled by getFileStatusIcon
      const status = getSubtreeDocStatus(node);
      if (status === "none") return null;
      if (status === "missing") {
        return (
          <span
            style={{ color: "#f44336", marginLeft: "4px" }}
            title="Contiene documentos sin archivo"
          >
            ⚠️
          </span>
        );
      }
      return (
        <span
          style={{ color: "#4caf50", marginLeft: "4px" }}
          title="Todos los documentos tienen archivo"
        >
          ✓
        </span>
      );
    };

    const isSelectable = () => {
      return isAsset || isDocument;
    };

    const isDownloadingNode =
      isDocument &&
      (localDownloading || node.node_id === downloadingDocumentId);

    return (
      <div style={{ marginLeft: `${depth * 20}px` }}>
        <div
          style={{
            cursor: isSelectable() ? "pointer" : "default",
            padding: "6px 8px",
            backgroundColor:
              node.node_id === selectedNodeId
                ? "#e3f2fd"
                : isHovered && isSelectable()
                ? "#f5f5f5"
                : "transparent",
            borderRadius: "4px",
            margin: "2px 0",
            display: "flex",
            alignItems: "center",
            gap: "8px",
            border:
              node.node_id === selectedNodeId
                ? "1px solid #2196f3"
                : "1px solid transparent",
            transition: "background-color 0.2s ease",
            opacity: isDownloadingNode ? 0.7 : 1,
          }}
          onClick={handleNodeClick}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
          title={`${getNodeTypeLabel()}: ${node.node_name}${
            !hasFile ? " (Sin archivo)" : ""
          }`}
        >
          {/* Toggle button for expandable nodes */}
          {(node.children && node.children.length > 0) ||
          node.node_type === "company" ? (
            <span
              onClick={handleToggleExpand}
              style={{
                cursor: "pointer",
                minWidth: "20px",
                textAlign: "center",
                fontSize: "10px",
              }}
            >
              {isExpanded ? "▼" : "▶"}
            </span>
          ) : (
            <span style={{ minWidth: "20px", textAlign: "center" }}></span>
          )}

          {/* Node icon */}
          <span style={{ fontSize: "16px" }}>{getNodeIcon()}</span>

          {/* Node name with type indicator */}
          <div
            style={{
              flex: 1,
              display: "flex",
              alignItems: "center",
              gap: "8px",
            }}
          >
            <span
              style={{
                fontWeight: node.node_type === "company" ? "600" : "400",
                fontSize: node.node_type === "company" ? "16px" : "14px",
                opacity: isDocument && !hasFile ? 0.6 : 1,
              }}
            >
              {node.node_name}
              {isDocument && !hasFile && (
                <span
                  style={{
                    fontSize: "0.75rem",
                    color: "#999",
                    marginLeft: "4px",
                  }}
                >
                  (sin archivo)
                </span>
              )}
            </span>
            {!isCompany && (
              <span
                style={{
                  fontSize: "0.75rem",
                  color: "#666",
                  padding: "2px 6px",
                  backgroundColor: isAsset
                    ? "#e3f2fd"
                    : isDocument
                    ? hasFile
                      ? "#e8f5e9"
                      : "#ffebee"
                    : "#f5f5f5",
                  borderRadius: "3px",
                  opacity: isDocument && !hasFile ? 0.6 : 1,
                }}
              >
                {getNodeTypeLabel()}
              </span>
            )}
            {isAsset && assetData?.codeIdentifier && (
              <span
                style={{
                  fontSize: "0.75rem",
                  color: "#666",
                  marginLeft: "4px",
                }}
              >
                ({assetData.codeIdentifier})
              </span>
            )}
            {isDocument && documentData?.version && (
              <span
                style={{
                  fontSize: "0.75rem",
                  color: "#666",
                  marginLeft: "4px",
                }}
              >
                v{documentData.version}
              </span>
            )}
            {isDocument && getFileStatusIcon()}
            {getParentFileStatusIcon()}
            {isDownloadingNode && (
              <span
                style={{
                  fontSize: "0.75rem",
                  color: "#2196f3",
                  marginLeft: "4px",
                }}
              >
                Descargando...
              </span>
            )}
          </div>
        </div>

        {/* Render children if expanded */}
        {isExpanded && node.children && node.children.length > 0 && (
          <div>
            {node.children.map((child) => (
              <TreeNodeItem
                key={`${child.node_type}-${child.node_id}`}
                node={child}
                depth={depth + 1}
                onSelect={onSelect}
                onDownloadDocument={onDownloadDocument}
                onToggleExpand={onToggleExpand}
              />
            ))}
          </div>
        )}

        {/* Placeholder for empty folders */}
        {isExpanded && (!node.children || node.children.length === 0) && (
          <div
            style={{
              marginLeft: `${(depth + 1) * 20}px`,
              color: "#999",
              fontStyle: "italic",
              padding: "6px",
              fontSize: "0.875rem",
            }}
          >
            No hay elementos
          </div>
        )}
      </div>
    );
  };

  return (
    <div
      style={{
        fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
        fontSize: "14px",
        minHeight: "400px",
      }}
    >
      {nodes.length === 0 ? (
        <div style={{ textAlign: "center", padding: "40px", color: "#666" }}>
          No hay datos para mostrar
        </div>
      ) : (
        nodes.map((node) => (
          <TreeNodeItem
            key={`${node.node_type}-${node.node_id}`}
            node={node}
            onSelect={onSelect}
            onDownloadDocument={onDownloadDocument}
            onToggleExpand={onToggleExpand}
          />
        ))
      )}
    </div>
  );
}

const AssetTreeList = () => {
  const [search, setSearch] = useState<string>("");
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [asset, setAsset] = useState<Asset | undefined>(undefined);
  const [loading, setLoading] = useState(true);
  const [assets, setAssets] = useState<Asset[]>([]);
  const [documents, setDocuments] = useState<Document[]>([]);
  const [treeData, setTreeData] = useState<Node[]>([]);
  const [selectedNode, setSelectedNode] = useState<Node | null>(null);
  const [expandedNodeIds, setExpandedNodeIds] = useState<number[]>([]);
  const [downloadingDocumentId, setDownloadingDocumentId] = useState<
    number | null
  >(null);
  const [isSearching, setIsSearching] = useState(false);
  const history = useHistory();

  // Get company from Redux store
  const company = useSelector((state: RootState) => state.company.data.company);

  // Function to download a document - BASADA EN LA PRIMERA VISTA
  const downloadDocument = async (doc: Document) => {
    try {
      if (!doc || !doc.id) {
        throw new Error("Documento inválido");
      }

      // Agregar el ID al Set
      setDownloadingDocumentId(doc.id);

      const downloadUrl = getAbsoluteUrl(
        `/documents/${doc.id}/get_document_url/`
      );

      const downloadResponse = await fetchAuthenticated(downloadUrl, {
        method: "GET",
      });

      if (!downloadResponse.ok) {
        throw new Error(
          `Error HTTP ${downloadResponse.status}: ${downloadResponse.statusText}`
        );
      }

      // Obtener el blob del archivo
      const blob = await downloadResponse.blob();

      // Verificar si el blob está vacío
      if (blob.size === 0) {
        throw new Error("El archivo descargado está vacío o no existe.");
      }

      // Crear URL para el blob
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;

      // Usar filePath del documento o un nombre por defecto
      const fileName = doc.filePath?.split("/").pop() || `documento_${doc.id}`;
      link.download = fileName;

      // Descargar el archivo
      document.body.appendChild(link);
      link.click();
      link.remove();

      // Liberar memoria
      window.URL.revokeObjectURL(url);

      // Mostrar mensaje de éxito
      successAlert(`Documento "${doc.name}" descargado exitosamente`);
    } catch (error) {
      console.error("Error al descargar:", error);

      let errorMessage = `Error al descargar el documento "${doc?.name}": `;

      if (error instanceof Error) {
        errorMessage += error.message;
      } else {
        errorMessage += String(error);
      }

      warningAlert(errorMessage);
    } finally {
      // Remover el ID del Set después de la descarga
      setDownloadingDocumentId(null);
    }
  };

  // Function to fetch data and build the tree
  const buildTreeData = useCallback(async () => {
    if (!company || !company.id) {
      setTreeData([]);
      return;
    }

    try {
      setLoading(true);

      // Fetch assets for this company
      const assetsUrlParams = new Map<string, string | undefined>();
      assetsUrlParams.set("company_id", company.id.toString());
      if (search) {
        assetsUrlParams.set("search", search);
      }

      const assetsStatus = await getList<Asset>(
        "/assets/",
        1000,
        0,
        assetsUrlParams
      );

      // Fetch documents for this company - IMPORTANTE: Incluir filePath
      const documentsUrlParams = new Map<string, string | undefined>();
      documentsUrlParams.set("company_id", company.id.toString());
      // Añadir parámetro para incluir todos los campos
      documentsUrlParams.set("include", "file_path,asset_id,version");
      if (search) {
        documentsUrlParams.set("search", search);
      }

      const documentsStatus = await getList<Document>(
        "/documents/",
        1000,
        0,
        documentsUrlParams
      );

      let fetchedAssets: Asset[] = [];
      let fetchedDocuments: Document[] = [];

      if (assetsStatus.status === SUCCESS && assetsStatus.data) {
        fetchedAssets = assetsStatus.data.items;
        setAssets(fetchedAssets);
      }

      if (documentsStatus.status === SUCCESS && documentsStatus.data) {
        fetchedDocuments = documentsStatus.data.items;
        console.log(
          "Documentos cargados para el árbol:",
          fetchedDocuments.map((doc) => ({
            id: doc.id,
            name: doc.name,
            filePath: doc.filePath,
            hasValidPath: !!doc.filePath && doc.filePath.trim() !== "",
          }))
        );
        setDocuments(fetchedDocuments);
      }

      // Create company node
      const companyNode = createCompanyNode(
        company.id,
        company.fantasyName || company.legalName || "Compañía",
        company
      );

      // Create asset nodes for this company
      const assetNodes: Node[] = [];
      const assetMap = new Map<number, Node>();

      // First pass: create all asset nodes
      fetchedAssets.forEach((assetItem) => {
        if (
          assetItem.id &&
          (!assetItem.parentAssetId || assetItem.parentAssetId === 0)
        ) {
          const assetNode = createAssetNode(
            assetItem.id,
            assetItem.name || "Sin nombre",
            assetItem
          );

          if (assetItem.codeIdentifier) {
            assetNode.node_name = `${assetItem.codeIdentifier} - ${assetNode.node_name}`;
          }

          assetNodes.push(assetNode);
          assetMap.set(assetItem.id, assetNode);
        }
      });

      // Second pass: build asset hierarchy
      fetchedAssets.forEach((assetItem) => {
        if (
          assetItem.id &&
          assetItem.parentAssetId &&
          assetItem.parentAssetId > 0
        ) {
          const parentNode = assetMap.get(assetItem.parentAssetId);
          if (parentNode) {
            const assetNode = createAssetNode(
              assetItem.id,
              assetItem.name || "Sin nombre",
              assetItem
            );

            if (assetItem.codeIdentifier) {
              assetNode.node_name = `${assetItem.codeIdentifier} - ${assetNode.node_name}`;
            }

            parentNode.children.push(assetNode);
            assetMap.set(assetItem.id, assetNode);
          }
        }
      });

      // Add documents to their respective assets
      fetchedDocuments.forEach((documentItem) => {
        if (documentItem.id) {
          const documentNode = createDocumentNode(
            documentItem.id,
            documentItem.name || "Sin nombre",
            documentItem
          );

          if (documentItem.assetId) {
            // Document belongs to an asset
            const parentAssetNode = assetMap.get(documentItem.assetId);
            if (parentAssetNode) {
              parentAssetNode.children.push(documentNode);
            } else {
              // If asset not found, add to company
              companyNode.children.push(documentNode);
            }
          } else {
            // Document without asset - add directly to company
            companyNode.children.push(documentNode);
          }
        }
      });

      // Add asset nodes to company
      companyNode.children.push(...assetNodes);

      // Sort company children: assets first, then documents
      companyNode.children.sort((a, b) => {
        if (a.node_type === "asset" && b.node_type === "document") return -1;
        if (a.node_type === "document" && b.node_type === "asset") return 1;
        return a.node_name.localeCompare(b.node_name);
      });

      // Expand company node by default
      companyNode.isExpanded = true;
      setExpandedNodeIds([company.id]);

      setTreeData([companyNode]);
    } catch (error) {
      console.error("Error building tree:", error);
      warningAlert("Error al construir el árbol de datos");
    } finally {
      setLoading(false);
      setIsSearching(false);
    }
  }, [company, search]);

  // Get current search parameters from URL
  const getSearchParams = useCallback(() => {
    const urlParams = new URLSearchParams(history.location.search);
    const searchParam = urlParams.get("search") || "";
    return searchParam;
  }, [history.location.search]);

  const fetchTreeData = async () => {
    await buildTreeData();
  };

  const onAddClick = () => {
    setShowEditModal(true);
  };

  const onUpdateClick = (asset: Asset) => {
    setAsset(asset);
    setShowEditModal(true);
  };

  const onDeleteClick = (asset: Asset) => {
    setAsset(asset);
    setShowDeleteModal(true);
  };

  const onAssetEditCancel = () => {
    setAsset(undefined);
    setShowEditModal(false);
  };

  const onAssetEditSuccess = () => {
    successAlert("Datos guardados con éxito!");
    setShowEditModal(false);
    setLoading(true);
    fetchTreeData();
  };

  const onAssetDeleteCancel = () => {
    setAsset(undefined);
    setShowDeleteModal(false);
  };

  const onAssetDeleteSuccess = () => {
    successAlert("Registro eliminado con éxito!");
    setShowDeleteModal(false);
    setLoading(true);
    fetchTreeData();
  };

  const onSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(e.target.value);
  };

  const onSearch = () => {
    if (isSearching) return;

    setIsSearching(true);
    const urlParams = new URLSearchParams();
    if (search.trim()) {
      urlParams.set("search", search.trim());
    }

    const newUrl = urlParams.toString() ? `?${urlParams.toString()}` : "";
    history.push(newUrl);

    fetchTreeData();
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === "Enter") {
      event.preventDefault();
      onSearch();
    }
  };

  const handleNodeSelect = (node: Node) => {
    setSelectedNode(node);

    // For assets, you might want to open edit modal or show details
    if (isAssetNode(node)) {
      const selectedAsset = assets.find((a) => a.id === node.node_id);
      if (selectedAsset) {
        console.log("Selected asset:", selectedAsset);
        // You could open asset details or edit modal here
      }
    }
    // For documents, download is handled directly in TreeNodeItem
  };

  const handleDownloadDocument = async (doc: Document) => {
    await downloadDocument(doc);
  };

  const toggleNodeExpansion = useCallback((nodeId: number) => {
    setExpandedNodeIds((prev) => {
      if (prev.includes(nodeId)) {
        return prev.filter((id) => id !== nodeId);
      } else {
        return [...prev, nodeId];
      }
    });
  }, []);

  const getTreeStatistics = () => {
    if (treeData.length === 0)
      return { assets: 0, documents: 0, documentsWithFile: 0 };

    const companyNode = treeData[0];
    let assetCount = 0;
    let documentCount = 0;
    let documentsWithFile = 0;

    const countNodes = (nodes: Node[]) => {
      nodes.forEach((node) => {
        if (isAssetNode(node)) {
          assetCount++;
        } else if (isDocumentNode(node)) {
          documentCount++;
          const documentData = getNodeData(node) as Document;
          if (documentData?.filePath && documentData.filePath.trim() !== "") {
            documentsWithFile++;
          }
        }
        if (node.children.length > 0) {
          countNodes(node.children);
        }
      });
    };

    countNodes(companyNode.children);

    return { assets: assetCount, documents: documentCount, documentsWithFile };
  };

  // Effect to load initial data and sync search with URL
  useEffect(() => {
    if (company && company.id) {
      const currentSearch = getSearchParams();
      setSearch(currentSearch);
      fetchTreeData();
    }
  }, [company, getSearchParams]);

  // Solo sincronizar desde URL cuando cambie la URL (no cuando cambie company)
  useEffect(() => {
    const unlisten = history.listen((location) => {
      if (location.pathname === getRoutePath("assetTree")) {
        const newSearch =
          new URLSearchParams(location.search).get("search") || "";
        setSearch(newSearch);
      }
    });

    return unlisten;
  }, [history]);

  const statistics = getTreeStatistics();

  return (
    <>
      <CRow>
        <CCol lg="12">
          <CCard>
            <CCardHeader className="d-flex flex-row mb-3">
              <div className="p-2">
                <h3>Árbol de Productores y Documentos</h3>
                <small className="text-muted">
                  Estructura jerárquica de{" "}
                  {company?.fantasyName || company?.legalName || "la compañía"}
                </small>
              </div>

              <PermissionRequiredComponent permissionName={ASSETS_CREATE}>
                <div className="p-2 ml-auto">
                  <CButton
                    color="primary"
                    className="float-right"
                    onClick={onAddClick}
                  >
                    <BsFillPlusCircleFill />
                    &nbsp; Agregar Nuevo
                  </CButton>
                </div>
              </PermissionRequiredComponent>
            </CCardHeader>
            <CCardBody>
              <CRow className="mb-4">
                <CCol lg="6">
                  <CLabel
                    className="visually-hidden"
                    htmlFor="autoSizingInputGroup"
                  ></CLabel>
                  <CInputGroup>
                    <CInputGroupText>
                      <i className="fa fa-search"></i>
                    </CInputGroupText>
                    <CInput
                      type="text"
                      placeholder="Buscar productores o documentos..."
                      onChange={onSearchChange}
                      value={search}
                      onKeyDown={handleKeyDown}
                    />
                    <CButton
                      type="button"
                      color="primary"
                      variant="outline"
                      onClick={onSearch}
                      disabled={isSearching}
                    >
                      {isSearching ? (
                        <>
                          <i className="fa fa-spinner fa-spin"></i> Buscando...
                        </>
                      ) : (
                        "Buscar"
                      )}
                    </CButton>
                  </CInputGroup>
                </CCol>
                <CCol
                  lg="6"
                  className="d-flex align-items-center justify-content-end"
                >
                  <div className="d-flex align-items-center gap-3">
                    {selectedNode && !isCompanyNode(selectedNode) && (
                      <div>
                        <small className="text-muted">
                          {isAssetNode(selectedNode)
                            ? "Productor seleccionado: "
                            : "Documento seleccionado: "}
                          <strong>{selectedNode.node_name}</strong>
                          {isAssetNode(selectedNode) && (
                            <CButton
                              color="link"
                              size="sm"
                              className="p-0 ml-2"
                              onClick={() => {
                                const selectedAsset = assets.find(
                                  (a) => a.id === selectedNode.node_id
                                );
                                if (selectedAsset) {
                                  onUpdateClick(selectedAsset);
                                }
                              }}
                            >
                              <small>Editar</small>
                            </CButton>
                          )}
                        </small>
                      </div>
                    )}

                    {/* Statistics */}
                    <div className="d-flex gap-2">
                      <span className="badge bg-info">
                        <i className="fa fa-cube me-1"></i>
                        {statistics.assets} Productores
                      </span>
                      <span className="badge bg-success">
                        <i className="fa fa-file me-1"></i>
                        {statistics.documentsWithFile}/{statistics.documents}{" "}
                        Docs
                      </span>
                    </div>

                    {search && (
                      <div>
                        <small className="text-muted">
                          Filtrado por: <strong>"{search}"</strong>
                          <CButton
                            color="link"
                            size="sm"
                            className="p-0 ml-2"
                            onClick={() => {
                              setSearch("");
                              history.push("");
                              fetchTreeData();
                            }}
                          >
                            <small>Limpiar</small>
                          </CButton>
                        </small>
                      </div>
                    )}
                  </div>
                </CCol>
              </CRow>

              {/* Tree view */}
              <div style={{ marginTop: "20px" }}>
                {loading ? (
                  <div className="text-center py-5">
                    <div
                      className="spinner-border text-primary"
                      role="status"
                    ></div>
                    <p className="mt-2">Cargando estructura del árbol...</p>
                  </div>
                ) : treeData.length > 0 ? (
                  <div
                    style={{
                      border: "1px solid #e0e0e0",
                      padding: "20px",
                      borderRadius: "8px",
                      backgroundColor: "#ffffff",
                      minHeight: "500px",
                      maxHeight: "600px",
                      overflowY: "auto",
                    }}
                  >
                    <AssetTreeView
                      nodes={treeData}
                      onSelect={handleNodeSelect}
                      onDownloadDocument={handleDownloadDocument}
                      selectedNodeId={selectedNode?.node_id}
                      expandedNodeIds={expandedNodeIds}
                      downloadingDocumentId={downloadingDocumentId}
                      onToggleExpand={toggleNodeExpansion}
                    />
                    <div className="mt-3 pt-3 border-top text-muted small">
                      <p className="mb-1">
                        <strong>Nota:</strong>
                        <span style={{ color: "#4caf50" }}> 📄 Verde</span> =
                        Documento con archivo |
                        <span style={{ color: "#f44336" }}> 📄 Rojo</span> =
                        Documento sin archivo |
                        <span style={{ color: "#4caf50" }}> ✓</span> = Archivo
                        disponible |
                        <span style={{ color: "#f44336" }}> ⚠️</span> = Sin
                        archivo |<span style={{ color: "#2196f3" }}> ⏳</span> =
                        Descargando
                      </p>
                      <p className="mb-0">
                        Haz clic en un productor para seleccionarlo, haz clic en un
                        documento para descargarlo.
                      </p>
                      <p className="mb-0 text-muted">
                        <small>
                          Los documentos sin archivo no se pueden descargar.
                        </small>
                      </p>
                    </div>
                  </div>
                ) : !company || !company.id ? (
                  <div className="text-center py-5">
                    <h4>No hay compañía seleccionada</h4>
                    <p>
                      Selecciona una compañía para ver su estructura de productores
                      y documentos.
                    </p>
                  </div>
                ) : (
                  <div className="text-center py-5">
                    <h4>No hay datos para mostrar</h4>
                    <p>
                      {search
                        ? `No se encontraron resultados para "${search}"`
                        : "Comienza agregando un nuevo productor o documento"}
                    </p>
                    {search && (
                      <CButton
                        color="primary"
                        variant="outline"
                        onClick={() => {
                          setSearch("");
                          history.push("");
                          fetchTreeData();
                        }}
                      >
                        Mostrar todos
                      </CButton>
                    )}
                  </div>
                )}
              </div>
            </CCardBody>
          </CCard>
        </CCol>
      </CRow>

      {/* Modals (keep your existing modals here) */}
      {/*
      <AssetEditModal
        show={showEditModal}
        onCancel={onAssetEditCancel}
        onSuccess={onAssetEditSuccess}
        asset={asset}
      />
      
      <AssetDeleteModal
        show={showDeleteModal}
        onCancel={onAssetDeleteCancel}
        onSuccess={onAssetDeleteSuccess}
        asset={asset}
      />
      */}
    </>
  );
};

export default AssetTreeList;
